import { useMutation, useQuery } from '@apollo/client'
import { PublisherInterface } from '@chili-publish/publisher-interface'
import { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import useAuth from 'data/gradoo/hooks/useAuth'
import { SAVE_DOCUMENT } from 'data/layoutcreator/mutations/projects'
import { GET_PAGE_DATA } from 'data/layoutcreator/queries/projects'
import ChiliFrames from 'shared/components/Tools/services/ChiliFrames'
import ChiliVariables from 'shared/components/Tools/services/ChiliVariables'

import { checkIfPageCreatedAutomatically } from './useEditorAutomationPageTransform'

const variablesApi = ChiliVariables.getInstance()
const framesApi = ChiliFrames.getInstance()

// Just a usefull hook whenever we need to fix something on the FE side
export const useEditorMigration = ({
  publisherConnector,
  isDocumentRendered
}: {
  publisherConnector: PublisherInterface | null
  isDocumentRendered: boolean
}) => {
  const { pathname } = useLocation()
  const projectId = pathname.split('/')[1]
  const { pageId: documentId } = useParams()
  const { authGroupId: groupId } = useAuth()

  const [saveDocument] = useMutation(SAVE_DOCUMENT)
  const { data: pageMetaData } = useQuery(GET_PAGE_DATA, {
    variables: {
      documentId: documentId!,
      projectId,
      groupId: groupId!
    }
  })

  const save = async () => {
    console.log('Saving the changes...')
    const isDirty = await publisherConnector?.getDirtyState()
    if (!isDirty) {
      return false
    }
    const tempXml = (await publisherConnector?.executeFunction(
      'document',
      'GetTempXML'
    )) as string

    const { data: savingData } = await saveDocument({
      variables: {
        projectId,
        documentId: documentId!,
        xml: tempXml
      }
    })

    if (savingData?.saveDocument) {
      console.log('%cDocument saved successfully!', 'color: green;')
    }
  }

  // Makes sure the page number is correct on evert editor launch
  const adjustPageNumber = async () => {
    if (pageMetaData) {
      const pageIndex = pageMetaData?.getPageData?.index
      if (pageIndex !== undefined) {
        await variablesApi.setVariableValue(
          'page_number',
          `${pageIndex + 3}`
        )
        console.log('Page number set to:', pageIndex + 1)
      }
    } else {
      console.error('Page metadata is not available')
    }
  }

  // For the pages created automatically, that was affected by page_number unlinking bug
  // Affects only 4-5 project. Could be removed soon
  // Date created: 23.04.2026
  const linkPageNumberToVariable = async () => {
    if (!(await checkIfPageCreatedAutomatically())) {
      return
    }

    return framesApi.goThroughFrames(async frame => {
      console.log('frame', frame)
      if (frame.type !== 'text') {
        return
      }
      console.log(0)
      if (frame.tag !== 'page_number') {
        return
      }

      console.log(1)

      console.log('frame.isVariable', frame.isVariable)
      if (frame.isVariable === "true") {
        return
      }

      const textFlow = frame.textFlow
      if (!textFlow) {
        return
      }

      console.log('textFlow', textFlow)

      const parser = new DOMParser()
      const doc = parser.parseFromString(textFlow, 'application/xml')
      const root = doc.querySelector('TextFlow')

      function getDeepestLastElement(node: any) {
        let current = node

        while (current.lastElementChild) {
          current = current.lastElementChild
        }

        return current
      }

      const lastChildElement = getDeepestLastElement(root)
      console.log('lastChildElement', lastChildElement)

      if (!lastChildElement) {
        return
      }

      lastChildElement.textContent = `%page_number%`
      // Removing siblings since variable name could be splitted between different span elements by Chili
      const siblings = Array.from(lastChildElement.parentNode?.children ?? []).filter(
        (element) => element !== lastChildElement
      );
      console.log('siblings', siblings)
      siblings.forEach((sibling: any) =>
        lastChildElement.parentNode.removeChild(sibling)
      )

      const resultString = new XMLSerializer().serializeToString(doc)

      await publisherConnector?.executeFunction(
        frame.javaScriptDescriptor,
        'ImportTextFlow',
        resultString
      )
      publisherConnector?.setProperty(
        frame.javaScriptDescriptor,
        'isVariable',
        'true'
      )
    })
  }

  useEffect(() => {
    if (!isDocumentRendered) {
      return
    }

    const migrationWrapper = async () => {
      await linkPageNumberToVariable()
      await adjustPageNumber()
      await save()
    }

    migrationWrapper()
  }, [pageMetaData, isDocumentRendered])
}
